import { Component, OnInit, EventEmitter, Input, Output, DestroyRef } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import * as _ from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { ISelectOption } from 'src/app/models/selectOptions';
import { ISelectOptionGroups } from 'src/app/models/selectOptions/selectOptionGroups.interface';

import { FilterTypesEnum } from 'src/app/models/filters/filterTypes.enum';
import { debounceTime, distinctUntilChanged, filter, finalize, switchMap, tap } from 'rxjs';
import { createDepartmentVerticalFilter } from 'src/app/common/departmentFilterUtils';
import {
  SearchActivityHttpService,
  AutocompleteField,
} from 'src/app/services/httpServices/detection/searchActivityService/search-activity-http.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AiInLineActivityFiltersModel } from 'src/app/models/filters/implementations/pageFilters/aiInLineActivityFilters.model';

const DEFAULT_DEBOUNCE = 1000;
const MIN_CHARS_FOR_FILTER = 3;
@Component({
  selector: 'app-ai-inline-activity-vertical-filters',
  templateUrl: './ai-inline-activity-vertical-filters.component.html',
  styleUrls: ['./ai-inline-activity-vertical-filters.component.scss'],
})
export class AiInlineActivityVerticalFiltersComponent implements OnInit {
  @Input() public pageFilters: AiInLineActivityFiltersModel;
  @Input() public withCategories = true;
  @Input() public isFiltersMenuOpen: boolean;
  @Output() public filtersOnMenuStateChange = new EventEmitter<boolean>();

  @Output() public optionOnSelection = new EventEmitter<ISelectOption[]>();

  public optionsInsideCategories$: BehaviorSubject<ISelectOptionGroups[]> = new BehaviorSubject<ISelectOptionGroups[]>(
    []
  );
  public expandedCategories: { [optionGroup: string]: boolean } = {};
  public selectedIds: Set<string>;
  public isOpen = true;

  public selectedOptions: ISelectOption[] = [];

  public searchEnvironmentNameValuesCtrl = new UntypedFormControl();
  public filteredEnvironmentNameValues: ISelectOption[] = [];
  public isEnvironmentNameLoading$ = new BehaviorSubject(false);
  public environmentNameFilterEnum = FilterTypesEnum.Environment;

  public searchActorValuesCtrl = new UntypedFormControl();
  public filteredActorValues: ISelectOption[] = [];
  public searchThreadIdValuesCtrl = new UntypedFormControl();
  public filteredThreadIdValues: ISelectOption[] = [];
  public isActorLoading$ = new BehaviorSubject(false);
  public isThreadIdLoading$ = new BehaviorSubject(false);
  public actorFilterEnum = FilterTypesEnum.InLineActor;
  public threadIdFilterEnum = FilterTypesEnum.InLineThreadId;
  public searchAgentValuesCtrl = new UntypedFormControl();
  public filteredAgentValues: ISelectOption[] = [];
  public isAgentLoading$ = new BehaviorSubject(false);
  public agentNameFilterEnum = FilterTypesEnum.InLineAgent;

  public departmentFilter = createDepartmentVerticalFilter();

  public dynamicCategories: string[];

  constructor(
    private readonly _searchActivityHttpService: SearchActivityHttpService,
    private readonly _destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.dynamicCategories = this.pageFilters.getDynamicFilters();

    this.pageFilters
      .getAllFiltersOptions()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((options) => {
        const optionsInsideCategories: ISelectOptionGroups[] = [];
        const indexByCategory: { [key: string]: number } = {};

        options.forEach((option) => {
          if (option.categoryName) {
            if (option.categoryName in indexByCategory) {
              optionsInsideCategories[indexByCategory[option.categoryName]].options.push(option);
            } else if (!this.dynamicCategories.includes(option.categoryName)) {
              optionsInsideCategories.push({
                selectedCount: 0,
                displayName: option.categoryName,
                options: [option],
                isDynamic: false,
                isNew: false,
              });
              indexByCategory[option.categoryName] = optionsInsideCategories.length - 1;
            } else {
              optionsInsideCategories.push({
                selectedCount: 0,
                displayName: option.categoryName,
                options: [option],
                isDynamic: true,
                isNew: false,
              });
            }
          } else {
            optionsInsideCategories.push({
              options: [option],
            } as ISelectOptionGroups);
          }
        });
        this.dynamicCategories.forEach((category) => {
          optionsInsideCategories.push({
            selectedCount: 0,
            displayName: category,
            options: [],
            isDynamic: true,
          });
        });

        this.optionsInsideCategories$.next(optionsInsideCategories);
      });

    this.pageFilters.onUpdate$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      const newSelectedIds: Set<string> = new Set();

      this.pageFilters.getAllSelectedOptions().forEach((option) => {
        newSelectedIds.add(option.id);
      });
      this.selectedIds = newSelectedIds;
    });

    this._initActorFilter();
    this._initThreadIdFilter();
    this._initAgentFilter();
    this._initEnvironmentNameFilter();
  }

  public expandClicked(optionGroupDisplayName: string, status: boolean) {
    this.expandedCategories[optionGroupDisplayName] = status;
  }

  public onToggleClick() {
    this.filtersOnMenuStateChange.emit(!this.isFiltersMenuOpen);
    this.isOpen = !this.isOpen;
  }

  public tablesFiltersChange(selectedOptions: ISelectOption[]) {
    this.pageFilters.updateFilterFromUserSelection(undefined, selectedOptions);
  }

  public threadIdSubmitted(users: ISelectOption[] = []): void {
    this.pageFilters.threadIdFilters.updateOptions(
      _.union(this.pageFilters.threadIdFilters.getOptions$().value, users)
    );
    this.pageFilters.updateFilterFromUserSelection(undefined, _.union(this._getSelectedOptions(), users));
  }

  public actorSubmitted(actors: ISelectOption[] = []): void {
    this.pageFilters.actorFilters.updateOptions(_.union(this.pageFilters.actorFilters.getOptions$().value, actors));
    this.pageFilters.updateFilterFromUserSelection(undefined, _.union(this._getSelectedOptions(), actors));
  }

  public agentSubmitted(agents: ISelectOption[] = []): void {
    this.pageFilters.agentFilters.updateOptions(_.union(this.pageFilters.agentFilters.getOptions$().value, agents));
    this.pageFilters.updateFilterFromUserSelection(undefined, _.union(this._getSelectedOptions(), agents));
  }

  public onSelect(id: string): void {
    if (!this.selectedIds.has(id)) {
      this.selectedIds.add(id);
    } else {
      this.selectedIds.delete(id);
    }
    this.optionOnSelection.emit(this._getSelectedOptions());
  }

  private _getSelectedOptions(): ISelectOption[] {
    const selectedIndexArray = Array.from(this.selectedIds);
    const optionsById = _.keyBy(this.pageFilters.getAllFiltersOptions().value, 'id');

    /// This is needed because there are options that can be selected but are not shown.
    const selectedOptions = selectedIndexArray.map((id: string) => optionsById[id]);

    return selectedOptions.filter((options) => typeof options !== 'undefined');
  }

  private _initFilter(
    control: UntypedFormControl,
    field: AutocompleteField,
    filteredValues: ISelectOption[],
    loadingSubject: BehaviorSubject<boolean>,
    filterType: FilterTypesEnum,
    transformDisplayName?: (text: string) => string
  ) {
    control.valueChanges
      .pipe(
        filter((res) => res !== null && res?.length >= MIN_CHARS_FOR_FILTER),
        distinctUntilChanged(),
        debounceTime(DEFAULT_DEBOUNCE),
        tap(() => {
          filteredValues.length = 0;
          loadingSubject.next(true);
        }),
        switchMap((value) =>
          this._searchActivityHttpService.getAutocompleteOptions('steps', field, value).pipe(
            finalize(() => {
              loadingSubject.next(false);
            })
          )
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((response) => {
        for (const suggestion of response.suggestions) {
          filteredValues.push({
            id: `${filterType}-${suggestion.value}`,
            value: suggestion.value,
            displayName: transformDisplayName ? transformDisplayName(suggestion.value) : suggestion.value,
            filterType: filterType,
            categoryName: filterType,
            withoutBeautifyDisplayName: true,
          } as ISelectOption);
        }
      });
  }

  private _initThreadIdFilter() {
    this.searchThreadIdValuesCtrl.valueChanges
      .pipe(
        filter((res) => res !== null && res?.length >= MIN_CHARS_FOR_FILTER),
        distinctUntilChanged(),
        tap(() => {
          this.filteredThreadIdValues = [];
        }),
        tap((value) => {
          this.filteredThreadIdValues.push({
            id: value,
            value: value,
            displayName: value,
            filterType: FilterTypesEnum.InLineThreadId,
            categoryName: FilterTypesEnum.InLineThreadId,
            withoutBeautifyDisplayName: true,
          } as ISelectOption);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  private _initActorFilter() {
    this._initFilter(
      this.searchActorValuesCtrl,
      'actor',
      this.filteredActorValues,
      this.isActorLoading$,
      FilterTypesEnum.InLineActor
    );
  }

  private _initAgentFilter() {
    this._initFilter(
      this.searchAgentValuesCtrl,
      'agent',
      this.filteredAgentValues,
      this.isAgentLoading$,
      FilterTypesEnum.InLineAgent,
      (text) => text ?? ''
    );
  }

  private _initEnvironmentNameFilter() {
    this.searchEnvironmentNameValuesCtrl.valueChanges
      .pipe(
        filter((res) => res !== null && res?.length >= MIN_CHARS_FOR_FILTER),
        distinctUntilChanged(),
        tap(() => {
          this.filteredEnvironmentNameValues.length = 0;
        }),
        tap((value) => {
          this.filteredEnvironmentNameValues.push({
            id: value,
            value: value,
            displayName: value,
            filterType: FilterTypesEnum.Environment,
            categoryName: FilterTypesEnum.Environment,
            withoutBeautifyDisplayName: true,
          } as ISelectOption);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }
}
